import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import {
    Button,
    Form,
    Header,
    Icon,
    Input,
    Label,
    Message,
    Pagination,
    Table,
} from 'semantic-ui-react'
import { LOAD_AUTH_USERS_QUERY } from '../../../graphql/admin'
import LoadingSpinner from '../../LoadingSpinner'
import CreateUserModal from './CreateUserModal'
import {
    AUTH_USERS_PER_PAGE,
    formatRelativeDate,
    getInitials,
    getReferralPartner,
    getUserDisplayName,
} from './helpers'
import '../styles.css'

const AuthUsers = () => {
    const [page, setPage] = useState(1)
    const [queryInput, setQueryInput] = useState('')
    const [query, setQuery] = useState('')
    const [createModalOpen, setCreateModalOpen] = useState(false)

    const { data, loading, error, refetch } = useQuery(LOAD_AUTH_USERS_QUERY, {
        variables: {
            query,
            pagination: {
                page,
                per_page: AUTH_USERS_PER_PAGE,
            },
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    })

    const users = data?.authUsers?.users || []
    const total = data?.authUsers?.total || 0
    const totalPages = Math.max(Math.ceil(total / AUTH_USERS_PER_PAGE), 1)

    const onSearch = () => {
        setPage(1)
        setQuery(queryInput.trim())
    }

    const onReset = () => {
        setPage(1)
        setQuery('')
        setQueryInput('')
    }

    return (
        <div className="auth-users-page">
            <div className="auth-users-heading">
                <div>
                    <Header as="h2">Users</Header>
                    <p className="auth-user-muted">
                        Manage Dexter user identities, account access, auth emails,
                        and recent session history.
                    </p>
                </div>
                <Button
                    primary
                    icon
                    labelPosition="left"
                    onClick={() => setCreateModalOpen(true)}
                >
                    <Icon name="plus" />
                    Create user
                </Button>
            </div>

            <Form onSubmit={onSearch} className="auth-users-search">
                <Input
                    fluid
                    icon="search"
                    iconPosition="left"
                    placeholder="Search by full name, email, or user ID"
                    value={queryInput}
                    onChange={(e, { value }) => setQueryInput(value)}
                />
                <Button type="submit" primary>
                    Search
                </Button>
                <Button type="button" basic onClick={onReset}>
                    Reset
                </Button>
            </Form>

            {error && <Message negative content={error.message} />}
            {loading && !data ? (
                <LoadingSpinner />
            ) : (
                <>
                    <Table basic="very" className="auth-users-table">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>User</Table.HeaderCell>
                                <Table.HeaderCell>User ID</Table.HeaderCell>
                                <Table.HeaderCell>Status</Table.HeaderCell>
                                <Table.HeaderCell>Password</Table.HeaderCell>
                                <Table.HeaderCell>Latest Login</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {users.map((record) => {
                                const user = record.user
                                const referralPartner = getReferralPartner(user)
                                return (
                                    <Table.Row key={user.id}>
                                        <Table.Cell>
                                            <div className="auth-user-cell">
                                                <div className="auth-user-avatar">
                                                    {getInitials(user)}
                                                </div>
                                                <div>
                                                    <Link
                                                        to={`/admin/users/${user.id}`}
                                                        className="auth-user-name-link"
                                                    >
                                                        {getUserDisplayName(user)}
                                                    </Link>
                                                    {referralPartner && (
                                                        <Label
                                                            size="tiny"
                                                            color="teal"
                                                            className="auth-user-partner-label"
                                                        >
                                                            {referralPartner}
                                                        </Label>
                                                    )}
                                                    <div>
                                                        <Link
                                                            to={`/admin/users/${user.id}`}
                                                            className="auth-user-email-link"
                                                        >
                                                            {user.email}
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="auth-user-id">
                                                {user.id}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Label
                                                color={
                                                    record.isBlocked
                                                        ? 'red'
                                                        : record.emailVerified
                                                        ? 'green'
                                                        : 'yellow'
                                                }
                                                size="small"
                                            >
                                                {record.isBlocked
                                                    ? 'Blocked'
                                                    : record.emailVerified
                                                    ? 'Verified'
                                                    : 'Unverified'}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {record.passwordConfigured
                                                ? 'Configured'
                                                : 'Not configured'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {formatRelativeDate(record.lastLoginAt)}
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            })}
                            {!users.length && (
                                <Table.Row>
                                    <Table.Cell colSpan="5">
                                        <Message
                                            info
                                            content="No users match this search."
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                    <div className="auth-users-pagination">
                        <span className="auth-user-muted">
                            {total} user{total === 1 ? '' : 's'}
                        </span>
                        <Pagination
                            activePage={page}
                            totalPages={totalPages}
                            onPageChange={(e, { activePage }) =>
                                setPage(activePage)
                            }
                        />
                    </div>
                </>
            )}

            <CreateUserModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreated={refetch}
            />
        </div>
    )
}

export default AuthUsers
